import { useCallback, useEffect, useState } from "react";
import { HeartPulse, Loader2, Plus, X, Trash2, Users, BookOpen, Calendar } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import AudiencePicker from "../../components/os/AudiencePicker";
import { api } from "../../lib/api";

const CAT_TONE = {
  fisico: "bg-emerald-500/15 text-emerald-600", mental: "bg-violet/15 text-violet",
  nutricion: "bg-gold/15 text-gold", financiero: "bg-sky-500/15 text-sky-600",
  social: "bg-pink-500/15 text-pink-600", emocional: "bg-orange-500/15 text-orange-600",
};

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="wp-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

export default function WellPrograms() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);

  const load = useCallback(() => { api.get("/well-programs").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/well-programs/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ is_leader: false, areas: [], categories: [], category_labels: {} })); load(); }, [load]);

  const openNew = () => {
    setF({ title: "", description: "", category: "fisico", start_date: "", end_date: "",
      aud: { audience: "all", user_ids: [], areas: [] }, create_course: false, course_topics: "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!f.title.trim()) return toast.error("Escribe el título del programa");
    setBusy(true);
    try {
      await api.post("/well-programs", {
        title: f.title, description: f.description, category: f.category,
        audience: f.aud.audience, participant_ids: f.aud.user_ids, areas: f.aud.areas,
        start_date: f.start_date || null, end_date: f.end_date || null,
        create_course: f.create_course, course_topics: f.course_topics.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Programa de bienestar creado" + (f.create_course ? " · curso generándose en Academy" : ""));
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { try { await api.delete(`/well-programs/${id}`); load(); } catch { toast.error("Error"); } };
  const linkCourse = async (id) => { try { await api.post(`/well-programs/${id}/create-course`); toast.success("Curso creándose en Academy"); load(); } catch { toast.error("Error"); } };

  return (
    <OSLayout title="OMNIA Well Programs">
      <div className="max-w-4xl space-y-6" data-testid="wellprograms-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <p className="text-sm text-navy/55 max-w-xl">Crea programas de bienestar online. Cada programa puede generar y enlazar un curso en <b className="text-navy">OMNIA Academy</b> con contenido creado por IA.</p>
          {meta?.is_leader && <button onClick={openNew} className="btn btn-primary gap-2" data-testid="wp-new"><Plus className="w-4 h-4" /> Nuevo programa</button>}
        </div>

        {items.length === 0 ? (
          <div className="bg-white border border-navy/10 rounded-2xl p-10 text-center text-navy/40" data-testid="wp-empty">
            <HeartPulse className="w-8 h-8 mx-auto mb-2 text-navy/25" /> Aún no hay programas de bienestar.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {items.map((p) => (
              <div key={p.id} className="bg-white border border-navy/10 rounded-2xl p-5" data-testid={`wp-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${CAT_TONE[p.category] || "bg-navy/10 text-navy/50"}`}>{meta?.category_labels?.[p.category] || p.category}</span>
                    <div className="text-base font-medium text-navy">{p.title}</div>
                  </div>
                  {meta?.is_leader && <button onClick={() => del(p.id)} className="text-navy/30 hover:text-destructive" data-testid={`wp-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
                {p.description && <p className="text-sm text-navy/55 mt-1">{p.description}</p>}
                <div className="flex flex-wrap gap-3 mt-3 text-xs text-navy/50">
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {(p.participant_ids || []).length} participantes</span>
                  {(p.start_date || p.end_date) && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {p.start_date || "—"} → {p.end_date || "—"}</span>}
                  {p.course_id
                    ? <span className="inline-flex items-center gap-1 text-violet"><BookOpen className="w-3.5 h-3.5" /> Curso enlazado</span>
                    : meta?.is_leader && <button onClick={() => linkCourse(p.id)} className="inline-flex items-center gap-1 text-violet hover:underline" data-testid={`wp-link-course-${p.id}`}><BookOpen className="w-3.5 h-3.5" /> Crear curso en Academy</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && f && (
        <Modal title="Nuevo programa de bienestar" onClose={() => setOpen(false)}>
          <div className="space-y-4">
            <input className="field" placeholder="Título del programa" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="wp-title" />
            <textarea className="field" rows={2} placeholder="Descripción" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="wp-desc" />
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1"><label className="text-xs text-navy/60 mb-1 block">Categoría</label>
                <select className="field" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="wp-category">
                  {(meta?.categories || []).map((c) => <option key={c} value={c}>{meta?.category_labels?.[c] || c}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-navy/60 mb-1 block">Inicio</label><input type="date" className="field" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="wp-start" /></div>
              <div><label className="text-xs text-navy/60 mb-1 block">Fin</label><input type="date" className="field" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="wp-end" /></div>
            </div>

            <div>
              <label className="text-xs font-medium text-navy/70 mb-2 block">Audiencia</label>
              <AudiencePicker value={f.aud} onChange={(aud) => setF({ ...f, aud })} areas={meta?.areas || []} testid="wp-aud" />
            </div>

            <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer">
              <input type="checkbox" checked={f.create_course} onChange={(e) => setF({ ...f, create_course: e.target.checked })} data-testid="wp-create-course" />
              Generar un curso en OMNIA Academy para este programa
            </label>
            {f.create_course && <input className="field" placeholder="Temas del curso, separados por coma" value={f.course_topics} onChange={(e) => setF({ ...f, course_topics: e.target.value })} data-testid="wp-course-topics" />}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button>
              <button onClick={submit} disabled={busy} className="btn btn-primary gap-2" data-testid="wp-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear programa</button>
            </div>
          </div>
        </Modal>
      )}
    </OSLayout>
  );
}
